import {
  BlockOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  ControlOutlined,
  MoreOutlined,
  PlusOutlined,
  TableOutlined,
} from "@ant-design/icons";
import {
  useHasRole,
  useNavigateWithSearchParams,
} from "@p0security/frontend-core";
import { ErrorDisplay } from "@p0security/frontend-core/react";
import { Policy } from "@p0security/shared/types/policy/types";
import {
  Button,
  Dropdown,
  Empty,
  Flex,
  type MenuProps,
  Segmented,
  Spacing,
  Spin,
} from "@p0security/ui";
import { InstalledIntegrationsProvider } from "components/Jit/Policy/providers/InstalledIntegrationsProvider";
import { useLoadPolicies } from "components/Jit/Policy/useLoadPolicies";
import { useSubmitWorkflow } from "components/Jit/Policy/useSubmitWorkflow";
import PolicyTableView from "components/Jit/Policy/views/PolicyTableView";
import { RulesView } from "components/Jit/Policy/views/RulesView";
import yaml from "js-yaml";
import stringify from "json-stable-stringify";
import { noop } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { usePolicyStore } from "store/policyStore";
import { createEnumParam, useQueryParams, withDefault } from "use-query-params";

import { BundleList } from "./BundleList";
import { useAccessBundles } from "./useAccessBundles";

type ActiveView = "table" | "yaml";
type StatusFilter = "all" | "disabled" | "enabled";
type Category = "composite" | "control" | "governance";

const matchesFilters = (
  rule: any,
  category: Category,
  statusFilter: StatusFilter
): boolean => {
  if (category === "composite" && rule.resource?.service !== "bundle")
    return false;
  if (category === "control" && rule.resource?.service === "bundle")
    return false;
  if (statusFilter === "enabled" && !!rule.disabled) return false;
  if (statusFilter === "disabled" && !rule.disabled) return false;
  return true;
};

/** Categories not yet backed by data render a placeholder. */
const CATEGORY_PLACEHOLDER: Partial<Record<Category, string>> = {
  governance: "Governance policies are coming soon.",
};

const TAB_TO_CATEGORY: Record<string, Category> = {
  bundles: "composite",
  control: "control",
  governance: "governance",
};

const CATEGORY_TO_TAB: Record<Category, "bundles" | "governance" | undefined> =
  {
    composite: "bundles",
    control: undefined,
    governance: "governance",
  };

const STATUS_FILTER_VALUES: StatusFilter[] = ["all", "disabled", "enabled"];

export const PolicyStudioShell = () => {
  const [{ tab, status }, setQuery] = useQueryParams(
    {
      tab: createEnumParam(["bundles", "governance"]),
      status: withDefault(createEnumParam(STATUS_FILTER_VALUES), "all", true),
    },
    { removeDefaultsFromUrl: true }
  );
  const category = TAB_TO_CATEGORY[tab ?? ""] ?? "control";
  const statusFilter: StatusFilter = status ?? "all";

  const [activeView, setActiveView] = useState<ActiveView>("table");

  const setCategory = useCallback(
    (value: Category) => setQuery({ tab: CATEGORY_TO_TAB[value] }, "replaceIn"),
    [setQuery]
  );

  const setStatusFilter = useCallback(
    (value: StatusFilter) => setQuery({ status: value }, "replaceIn"),
    [setQuery]
  );
  const { errors, isLoading, jsonRules, currentVersion, setErrors } =
    usePolicyStore();
  const navigateWithSearchParams = useNavigateWithSearchParams();
  const canSubmit = useHasRole("owner");
  const submitWorkflow = useSubmitWorkflow();

  useLoadPolicies();

  const {
    list: bundleList,
    remove: removeBundle,
    setDisabled: setBundleDisabled,
  } = useAccessBundles(statusFilter);

  const handleAddNewPolicy = useCallback(
    () => navigateWithSearchParams("new"),
    [navigateWithSearchParams]
  );
  const handleAddNewBundle = useCallback(
    () => navigateWithSearchParams("bundles/new"),
    [navigateWithSearchParams]
  );

  const handleDeleteRule = useCallback(
    async (record: Policy) => {
      const updatedRules = jsonRules.filter(
        (rule) => stringify(rule) !== stringify(record)
      );
      await submitWorkflow({
        jsonData: { currentVersion, policy: { rules: updatedRules } },
        shouldNavigate: false,
        successMessage: "Policy was successfully deleted.",
      });
    },
    [currentVersion, jsonRules, submitWorkflow]
  );

  const visibleRules = useMemo(
    () => jsonRules.filter((r) => matchesFilters(r, category, statusFilter)),
    [jsonRules, category, statusFilter]
  );

  const hiddenRules = useMemo(
    () => jsonRules.filter((r) => !matchesFilters(r, category, statusFilter)),
    [jsonRules, category, statusFilter]
  );

  const filteredYaml = useMemo(
    () =>
      visibleRules.length > 0 ? yaml.dump(visibleRules, { flowLevel: 4 }) : "",
    [visibleRules]
  );

  const handleSubmitWorkflow = useCallback(
    async (value: string) => {
      try {
        const parsed = yaml.load(value);
        if (!Array.isArray(parsed)) {
          throw new Error("Error parsing policies.");
        }
        await submitWorkflow({
          jsonData: {
            currentVersion,
            policy: { rules: [...hiddenRules, ...parsed] },
          },
          shouldNavigate: false,
        });
      } catch (err) {
        setErrors([String(err)]);
      }
    },
    [currentVersion, hiddenRules, setErrors, submitWorkflow]
  );

  const viewMenu = useMemo<MenuProps>(
    () => ({
      items: [
        {
          icon: <TableOutlined />,
          key: "table" satisfies ActiveView,
          label: "Table view",
        },
        {
          icon: <CodeOutlined />,
          key: "yaml" satisfies ActiveView,
          label: "YAML view",
        },
      ],
      onClick: ({ key }) => setActiveView(key as ActiveView),
      selectable: true,
      selectedKeys: [activeView],
    }),
    [activeView]
  );

  const placeholder = CATEGORY_PLACEHOLDER[category];
  const isComposite = category === "composite";

  return (
    <InstalledIntegrationsProvider>
      {errors.map((e, ix) => (
        <ErrorDisplay error={e} key={ix} onClose={noop} />
      ))}
      {isLoading ? (
        <Spin />
      ) : (
        <>
          <Spacing mb={16}>
            <Flex align="center" justify="space-between">
              <Flex align="center" gap="middle">
                <Segmented
                  options={[
                    { label: "All", value: "all" },
                    {
                      label: "Enabled",
                      value: "enabled",
                      icon: <CheckCircleOutlined />,
                    },
                    {
                      label: "Disabled",
                      value: "disabled",
                      icon: <CloseCircleOutlined />,
                    },
                  ]}
                  value={statusFilter}
                  onChange={setStatusFilter}
                />
                <Segmented
                  options={[
                    {
                      label: "Control",
                      value: "control",
                      icon: <ControlOutlined />,
                    },
                    // {
                    //   label: "Governance",
                    //   value: "governance",
                    //   icon: <SafetyOutlined />,
                    // },
                    {
                      label: "Access Bundles",
                      value: "composite",
                      icon: <BlockOutlined />,
                    },
                  ]}
                  value={category}
                  onChange={setCategory}
                />
              </Flex>
              <Flex align="center" gap={8}>
                {canSubmit && !placeholder && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={
                      isComposite ? handleAddNewBundle : handleAddNewPolicy
                    }
                  >
                    {isComposite ? "Create Bundle" : "Add New Policy"}
                  </Button>
                )}
                <Dropdown
                  menu={viewMenu}
                  trigger={["click"]}
                  placement="bottomRight"
                >
                  <Button
                    type="text"
                    icon={<MoreOutlined />}
                    aria-label="View options"
                  />
                </Dropdown>
              </Flex>
            </Flex>
          </Spacing>
          {placeholder ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={placeholder}
            />
          ) : activeView === "yaml" && isComposite ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Access bundles cannot be edited as YAML. Use the table view to manage bundles."
            />
          ) : activeView === "yaml" ? (
            <RulesView
              canSubmit={canSubmit}
              handleSubmitWorkflow={handleSubmitWorkflow}
              filteredYaml={filteredYaml}
            />
          ) : isComposite ? (
            <BundleList
              canSubmit={canSubmit}
              list={bundleList}
              remove={removeBundle}
              setDisabled={setBundleDisabled}
            />
          ) : (
            <PolicyTableView
              canSubmit={canSubmit}
              handleDelete={handleDeleteRule}
              readOnly={!canSubmit}
              rules={visibleRules}
            />
          )}
        </>
      )}
    </InstalledIntegrationsProvider>
  );
};
